import { useState } from 'react';
import { apiPost } from '../../lib/api-client';
import { applyTableQuery, nextSort, type ColumnSort } from '../../lib/list-query';
import { apiError } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { Alert } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ColumnFilterInputs, ColumnFilterToggle, useColumnSearch } from '../ui/ColumnSearch';
import { useNotice } from '../ui/Notice';
import { Dialog } from '../ui/Dialog';
import { SortHeader } from '../ui/chrome';
import { Input } from '../ui/Input';

export interface IssueRow {
  at?: string;
  actor?: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  body?: string;
  sent?: boolean;
  sharePointUrl?: string;
}

export function revisionLabel(revision: unknown): string {
  const number = Number(revision || 1);
  return `Rev ${Number.isFinite(number) && number > 0 ? number : 1}`;
}

export function IssueHistory({ issues, sharePointUrl }: { issues: unknown; sharePointUrl?: string }) {
  const rows = Array.isArray(issues) ? issues as IssueRow[] : [];
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ColumnSort | null>(null);
  const columnSearch = useColumnSearch();
  const columns = [
    { key: 'at', label: 'When', className: 'px-3 py-2 font-medium', text: (row: IssueRow) => when(row.at), sort: (row: IssueRow) => row.at || '' },
    { key: 'actor', label: 'Who', className: 'py-2 font-medium', text: (row: IssueRow) => row.actor || '' },
    { key: 'sent', label: 'Emailed', className: 'py-2 font-medium', text: (row: IssueRow) => row.sent ? 'Yes' : 'No' },
    { key: 'to', label: 'To', className: 'py-2 font-medium', text: (row: IssueRow) => (row.to || []).join(', ') },
    { key: 'cc', label: 'Cc', className: 'py-2 font-medium', text: (row: IssueRow) => (row.cc || []).join(', ') },
    { key: 'bcc', label: 'Bcc', className: 'py-2 font-medium', text: (row: IssueRow) => (row.bcc || []).join(', ') },
    { key: 'subject', label: 'Subject', className: 'py-2 font-medium', text: (row: IssueRow) => row.subject || '' },
    { key: 'share', label: 'SharePoint', className: 'py-2 font-medium', text: (row: IssueRow) => row.sharePointUrl || '' },
    { key: 'body', label: 'Body', className: 'py-2 pr-3 font-medium', text: (row: IssueRow) => row.body || '' },
  ];
  const shown = applyTableQuery(rows, search, columns, sort).filter((row) => columnSearch.matches(Object.fromEntries(columns.map((column) => [column.key, String(column.text(row) ?? '')]))));
  const current = openable(sharePointUrl);
  return (
    <div>
      <h3 className="text-lg font-semibold">Issue history</h3>
      {current ? <p className="mt-2 text-sm"><a className="text-blue-700 underline dark:text-blue-300" href={current} target="_blank" rel="noreferrer">Open in SharePoint</a></p> : null}
      {rows.length ? (
        <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="p-3"><Input className="h-8 max-w-xs" aria-label="Search issue history" placeholder="Search issue history" value={search} onChange={(event) => setSearch(event.target.value)} /></div>
          <table className="bo-cards min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700">
                {columns.map((column) => <SortHeader key={column.key} className={column.className} label={column.label} active={sort?.key === column.key} dir={sort?.dir || 'asc'} onSort={() => setSort((currentSort) => nextSort(currentSort, column.key))} />)}
                <th className="w-8 pr-2 text-right"><ColumnFilterToggle open={columnSearch.open} onClick={columnSearch.toggle} /></th>
              </tr>
              {columnSearch.open ? <ColumnFilterInputs columns={columns.map((column) => ({ key: column.key, label: column.label }))} draft={columnSearch.draft} onChange={columnSearch.set} trailing /> : null}
            </thead>
            <tbody>
              {shown.map((row, index) => (
                <tr key={`${row.at || ''}-${index}`} className="border-t border-gray-100 align-top dark:border-gray-800">
                  <td className="px-3 py-2 whitespace-nowrap" data-label="When">{when(row.at)}</td>
                  <td className="py-2 pr-3" data-label="Who">{row.actor || ''}</td>
                  <td className="py-2 pr-3" data-label="Emailed">{row.sent ? 'Yes' : 'No'}</td>
                  <td className="py-2 pr-3" data-label="To">{(row.to || []).join(', ')}</td>
                  <td className="py-2 pr-3" data-label="Cc">{(row.cc || []).join(', ')}</td>
                  <td className="py-2 pr-3" data-label="Bcc">{(row.bcc || []).join(', ')}</td>
                  <td className="py-2 pr-3" data-label="Subject">{row.subject || ''}</td>
                  <td className="py-2 pr-3" data-label="SharePoint">{openable(row.sharePointUrl) ? <a className="text-blue-700 underline dark:text-blue-300" href={openable(row.sharePointUrl)} target="_blank" rel="noreferrer">Open</a> : ''}</td>
                  <td className="py-2 pr-3 whitespace-pre-wrap" data-label="Body">{row.body || ''}</td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="mt-2 text-sm text-gray-500">No issues recorded.</p>}
    </div>
  );
}

function openable(value: string | undefined): string {
  return value && /^https?:\/\//i.test(value) ? value : '';
}

function when(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function MarkStatus({ id, url, status, onDone }: { id?: string; url: string; status?: string; onDone: (row: Record<string, unknown>) => void }) {
  const { user } = useAuth();
  const notice = useNotice();
  const [pending, setPending] = useState<'draft' | 'issued' | null>(null);
  const [error, setError] = useState('');
  if (!user?.flags?.is_admin || !id || !status) return null;
  const choices = (['draft', 'issued'] as const).filter((item) => item !== status);

  async function apply(next: 'draft' | 'issued') {
    setError('');
    try {
      onDone(await apiPost<Record<string, unknown>>(`${url}/${id}/status`, { status: next }));
      setPending(null);
      notice.success('Status updated');
    } catch (err) {
      notice.failure(apiError(err, 'Status change failed'));
    }
  }

  return (
    <>
      {choices.map((item) => (
        <Button key={item} variant="outline" onClick={() => setPending(item)}>{item === 'draft' ? 'Mark draft' : 'Mark issued'}</Button>
      ))}
      {pending ? (
        <Dialog
          title={pending === 'draft' ? 'Mark draft' : 'Mark issued'}
          onClose={() => setPending(null)}
          footer={<><Button variant="outline" onClick={() => setPending(null)}>Cancel</Button><Button onClick={() => void apply(pending)}>{pending === 'draft' ? 'Mark draft' : 'Mark issued'}</Button></>}
        >
          {error ? <Alert error>{error}</Alert> : null}
          <p className="text-sm text-gray-600 dark:text-gray-300">This only changes the status. It does not send email or file the document.</p>
        </Dialog>
      ) : null}
    </>
  );
}
